import { useMemo, useRef, useSyncExternalStore, } from 'react';
import { useToken, } from '@chakra-ui/react';
export function createTrendController(config, fragment) {
    const { legend: { controller: legendController }, tooltip: { controller: { trend: tooltipController }, }, } = fragment;
    function convertRemToPixels(rem) {
        return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
    }
    function subscribe(callback) {
        window.addEventListener('resize', callback);
        return () => {
            window.removeEventListener('resize', callback);
        };
    }
    function useDimensions(ref) {
        const dimensions = useSyncExternalStore(subscribe, () => JSON.stringify({
            width: ref.current?.offsetWidth ?? 0, // 0 is default width
            height: ref.current?.offsetHeight ?? 0, // 0 is default height
        }));
        return useMemo(() => JSON.parse(dimensions), [dimensions]);
    }
    function createVerticalLinePlugin(config) {
        return {
            id: 'verticalLinePlugin',
            beforeDatasetsDraw: (chart) => {
                const ctx = chart.ctx;
                const activePoint = chart.tooltip?.getActiveElements()[0];
                if (!activePoint)
                    return;
                const x = activePoint.element.x;
                const topY = chart.scales.y?.top;
                const bottomY = chart.scales.y?.bottom;
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x, topY);
                ctx.lineTo(x, bottomY);
                ctx.lineWidth = 1;
                ctx.strokeStyle = config.color;
                ctx.stroke();
                ctx.restore();
            },
        };
    }
    return {
        useProps(props) {
            const chartRef = useRef(null);
            const legend = legendController.useProps(props.legend);
            const containerRef = useRef(null);
            const dimensions = useDimensions(containerRef);
            const labels = useMemo(() => props.labels.map((item) => {
                const next = config.strategy.format.add(item, 1, props.axis.time.unit);
                const labelStart = config.strategy.format.formatShortDate(item, {
                    granularity: props.axis.time.unit,
                });
                const labelEnd = config.strategy.format.formatShortDate(next, {
                    granularity: props.axis.time.unit,
                });
                const full = props.axis.time.unit === 'day'
                    ? labelStart
                    : config.strategy.format.formatRange(item, next);
                return {
                    start: {
                        label: labelStart,
                        raw: item,
                    },
                    end: {
                        label: labelEnd,
                        raw: next,
                    },
                    range: {
                        label: full,
                    },
                };
            }), [props.labels]);
            const tooltipContent = tooltipController.useProps({
                chartRef,
                datasets: props.datasets,
                axes: props.axis,
                labels,
            });
            const colorsByDatasetIndex = useToken('colors', props.datasets.map((item) => item.color));
            const [gridBorderColor, axisLabelColor, axisTickColor] = useToken('colors', [
                props.theme.grid.borderColor,
                props.theme.axis.label.color,
                props.theme.axis.tick.color,
            ]);
            const axisLabelPaddingRaw = useToken('sizes', `${props.theme.axis.label.p}`);
            const axisLabelPadding = useMemo(() => convertRemToPixels(Number(axisLabelPaddingRaw.replace('rem', ''))), [axisLabelPaddingRaw]);
            const axisTickPaddingRaw = useToken('sizes', `${props.theme.axis.tick.p}`);
            const axisTickPadding = useMemo(() => convertRemToPixels(Number(axisTickPaddingRaw.replace('rem', ''))), [axisTickPaddingRaw]);
            // console.log('axisTickPadding', axisTickPadding);
            const plugins = useMemo(() => [createVerticalLinePlugin({ color: gridBorderColor })], [gridBorderColor]);
            const options = {
                animation: false,
                plugins: {
                    tooltip: tooltipContent.chartjs.tooltip,
                    legend: {
                        display: false,
                    },
                },
                interaction: {
                    ...tooltipContent.chartjs.interaction,
                    intersect: false,
                },
                clip: false,
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        type: 'category',
                        ticks: {
                        // padding: axisLabelPadding * 1,
                        },
                        grid: {
                            display: false,
                        },
                    },
                    y: {
                        position: props.axis.scalar.placement,
                        type: 'linear',
                        afterUpdate: (axis) => {
                            axis.paddingTop = 0;
                            axis.paddingBottom = 0;
                            if (props.axis.scalar.placement === 'left') {
                                axis.paddingLeft = 0;
                            }
                            if (props.axis.scalar.placement === 'right') {
                                axis.paddingRight = 0;
                            }
                        },
                        offset: true,
                        ticks: {
                            color: axisTickColor,
                            // currently there is an issue with padding applying in all directions and not
                            // just along the axis. we use the above afterUpdate callback to manually fix this
                            // after the fact
                            // https://github.com/chartjs/Chart.js/issues/9531
                            //
                            padding: axisTickPadding,
                        },
                        title: props.theme.axis.label.visible
                            ? {
                                display: true,
                                text: props.axis.scalar.label,
                                color: axisLabelColor,
                                padding: axisLabelPadding,
                            }
                            : undefined,
                        border: {
                            display: false,
                        },
                        grid: {
                            drawTicks: false,
                            color: gridBorderColor,
                        },
                    },
                },
                elements: {
                    point: {
                        radius: function (context) {
                            return 0;
                        },
                    },
                },
            };
            const data = {
                labels: labels.map((item) => item.start.label),
                datasets: useMemo(() => props.datasets
                    .filter((item) => props.legend.visible.value.includes(item.key))
                    .map((item, index) => {
                    const mapped = {
                        data: item.data,
                        label: item.name,
                        fill: false,
                        borderColor: colorsByDatasetIndex[index],
                        backgroundColor: colorsByDatasetIndex[index],
                        pointBackgroundColor: colorsByDatasetIndex[index],
                        pointBorderColor: colorsByDatasetIndex[index],
                        segment: {
                            borderDash: (ctx) => {
                                const indexStart = ctx.p0DataIndex;
                                const indexEnd = ctx.p1DataIndex;
                                const segment = props.series.getSegment({
                                    dataset: mapped,
                                    labels,
                                }, indexEnd);
                                return segment.variant === 'partial'
                                    ? [5, 5]
                                    : undefined;
                            },
                        },
                        spanGaps: config.options.line.spanGaps,
                    };
                    return mapped;
                }), [props.datasets, colorsByDatasetIndex]),
            };
            return {
                legend: legend,
                theme: props.theme,
                tooltip: { ...tooltipContent, Component: props.Tooltip },
                size: {
                    elementRef: containerRef,
                    width: dimensions.width,
                },
                chart: {
                    type: props.type === 'column' ? 'bar' : 'line',
                    data,
                    options,
                    plugins,
                },
            };
        },
    };
}
//# sourceMappingURL=trendController.js.map